import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Card, 
  Typography, 
  Autocomplete, 
  TextField, 
  CircularProgress, 
  Divider, 
  Skeleton, 
  Paper,
  InputAdornment 
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  BarChartOutlined, 
  Business, 
  Science as ScienceIcon 
} from '@mui/icons-material';
import { 
  Bar, 
  BarChart, 
  CartesianGrid, 
  Legend, 
  ResponsiveContainer, 
  Tooltip, 
  XAxis, 
  YAxis 
} from 'recharts';

import {
  getEdmDashboardComparativeMeasurementFacilityList,
  getEdmDashboardComparativeReport,
  getEdmDashboardComparativeReportFacilityList,
} from '../../../../helpers/api';
import { parseString4, properCase } from '../../../../helpers/utils';

// --- 1. MODERN CUSTOM TOOLTIP ---
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        elevation={4}
        sx={{
          p: 2,
          borderRadius: '12px',
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" color="textSecondary" fontWeight={700} mb={1}>
          {parseString4(label)}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={`item-${index}`} display="flex" alignItems="center" gap={1.5} mb={0.5}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color }} />
            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize', minWidth: 65 }}>
              {entry.name}:
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {Intl.NumberFormat('en-NG').format(entry.value)}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
};

export default function ComparativeAnalyticsEdmView({ stateId }) {
  // --- 2. CORE STATE & LOGIC (UNTOUCHED) ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [facilityData, setFacilityData] = useState([]);
  const [loadingFacilityData, setLoadingFacilityData] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');

  const [measurementData, setMeasurementData] = useState([]);
  const [loadingMeasurement, setLoadingMeasurement] = useState(false);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState();

  useEffect(() => {
    fetchFacilityList();
  }, [stateId]);

  useEffect(() => {
    fetchMeasurementList();
  }, []);

  useEffect(() => {
    fetch();
  }, [selectedMeasurementId, selectedFacilityId]);

  const fetch = () => {
    setLoading(true);
    getEdmDashboardComparativeReport(selectedFacilityId, selectedMeasurementId)
      .then((response) => {
        setLoading(false);
        setData(response.result.data || []);
      })
      .catch(() => setLoading(false));
  };

  const fetchFacilityList = () => {
    setLoadingFacilityData(true);
    getEdmDashboardComparativeReportFacilityList(stateId)
      .then((response) => {
        setLoadingFacilityData(false);
        const m = response?.result?.data?.map((item) => ({
          label: item.facilityName,
          value: item.facilityId,
        })) || [];
        setFacilityData(m);
        if (m.length > 0) setSelectedFacilityId(m[0].value);
      })
      .catch(() => setLoadingFacilityData(false));
  };

  const fetchMeasurementList = () => {
    setLoadingMeasurement(true);
    getEdmDashboardComparativeMeasurementFacilityList()
      .then((response) => {
        setLoadingMeasurement(false);
        const m = response.result.data?.map((item) => ({
          label: properCase(item.measurementName),
          value: item.measurementId,
        })) || [];
        setMeasurementData(m);
        if (m.length > 0) setSelectedMeasurementId(m[0].value);
      })
      .catch(() => setLoadingMeasurement(false));
  };

  // --- 3. CLEAN AUTOCOMPLETE RENDERING ---
  const renderFacilityList = () => {
    if (loadingFacilityData) {
      return <CircularProgress size={20} sx={{ mx: 2 }} />;
    }
    return (
      <Autocomplete
        disablePortal
        options={facilityData}
        value={facilityData.find((f) => f.value === selectedFacilityId) || null}
        getOptionLabel={(option) => option.label || ''}
        onChange={(e, val) => setSelectedFacilityId(val ? val.value : '')}
        slotProps={{ 
          paper: { 
            sx: { 
              borderRadius: '10px', 
              mt: 1, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e0e0e0' 
            } 
          } 
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            placeholder="Facility" 
            size="small" 
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Business sx={{ color: 'text.secondary', fontSize: 18, ml: 0.5 }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                bgcolor: '#f9fafb',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '& fieldset': { 
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': { 
                  borderColor: '#bdbdbd', 
                },
                '&.Mui-focused fieldset': { 
                  borderWidth: '1px',
                  borderColor: 'primary.main', 
                }
              }
            }} 
          />
        )}
        sx={{ width: { xs: '100%', sm: 260 } }}
      />
    );
  };

  const renderMeasurementList = () => {
    if (loadingMeasurement) {
      return <CircularProgress size={20} sx={{ mx: 2 }} />;
    }
    return (
      <Autocomplete
        disablePortal
        options={measurementData}
        value={measurementData.find((m) => m.value === selectedMeasurementId) || null}
        getOptionLabel={(option) => option.label || ''}
        onChange={(e, val) => setSelectedMeasurementId(val ? val.value : '')}
        slotProps={{ 
          paper: { 
            sx: { 
              borderRadius: '10px', 
              mt: 1, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e0e0e0' 
            } 
          } 
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            placeholder="Metric" 
            size="small" 
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <ScienceIcon sx={{ color: 'text.secondary', fontSize: 18, ml: 0.5 }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                bgcolor: '#f9fafb',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '& fieldset': { 
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': { 
                  borderColor: '#bdbdbd', 
                },
                '&.Mui-focused fieldset': { 
                  borderWidth: '1px',
                  borderColor: 'secondary.main', 
                }
              }
            }} 
          />
        )}
        sx={{ width: { xs: '100%', sm: 220 } }}
      />
    );
  };

  // --- 4. CHART CONTENT ---
  const renderChart = () => {
    if (loading) return <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} />;
    if (!data.length) {
      return (
        <Box height={320} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <BarChartOutlined sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
          <Typography color="textSecondary">No analytical data found for selection</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', height: 350, mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barSize={12} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha('#000', 0.05)} />
            <XAxis 
              dataKey="parameterName" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#666', fontSize: 11, fontWeight: 600 }} 
              interval={0}
              tickFormatter={(val) => parseString4(val)}
            />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#999', fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: alpha('#1976d2', 0.04) }} />
            <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '20px' }} />
            <Bar dataKey="normal" name="Normal" fill="#1976d2" radius={[4, 4, 0, 0]} />
            <Bar dataKey="qaqc" name="QA/QC" fill="#00ab55" radius={[4, 4, 0, 0]} />
            <Bar dataKey="nesrea" name="NESREA" fill="#ff4842" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  return (
    <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'divider', boxShadow: '0px 4px 20px rgba(0,0,0,0.03)', bgcolor: '#fff' }}>
      <Box sx={{ p: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Comparative Analytics</Typography>
          <Typography variant="body2" color="textSecondary">Emission comparison analytics</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          {renderMeasurementList()}
          {renderFacilityList()}
        </Box>
      </Box>
      <Divider />
      <Box sx={{ p: 3 }}>
        {renderChart()}
      </Box>
    </Card>
  );
}